import { RefObject, useEffect, useRef, useState } from 'react';
import { useHtmlStepperEditor } from './HtmlStepperEditor/HtmlStepperEditorContext';

type Device = 'desktop' | 'tablet' | 'mobile';

// Device viewport widths for simulating actual device rendering
const DEVICE_WIDTHS: Record<Device, number> = {
  desktop: 0, // 0 means use container width (no scaling)
  tablet: 768,
  mobile: 393, // iPhone 15/16 standard width
};

interface StepperPreviewProps {
  id?: string;
  iframeRef?: RefObject<HTMLIFrameElement | null>;
  device?: Device;
  title?: string;
}

export const StepperPreview = ({
  id,
  iframeRef,
  device = 'desktop',
  title = 'Stepper Preview',
}: StepperPreviewProps) => {
  const { iframe } = useHtmlStepperEditor();
  const { iframeSrc } = iframe;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Calculate the device width for viewport simulation
  const deviceWidth = DEVICE_WIDTHS[device];
  const needsScaling = deviceWidth > 0;

  // Measure container size for scaling calculations
  useEffect(() => {
    if (!containerRef.current || !needsScaling) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [needsScaling]);

  // Calculate scale factor
  const scale =
    needsScaling && containerSize.width > 0
      ? containerSize.width / deviceWidth
      : 1;

  // Calculate iframe height to fill container after scaling
  const iframeHeight =
    needsScaling && scale > 0 ? containerSize.height / scale : 0;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <iframe
        id={id}
        ref={iframeRef}
        className="stepper-preview-iframe"
        style={{
          border: 0,
          minHeight: 0,
          minWidth: 0,
          backgroundColor: 'white',
          ...(needsScaling
            ? {
                width: `${deviceWidth}px`,
                height: `${iframeHeight}px`,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }
            : {
                width: '100%',
                height: '100%',
              }),
        }}
        src={iframeSrc || undefined}
        title={title}
      />
    </div>
  );
};
